import React, { useEffect, useState } from 'react';
import { Form, Image } from 'react-bootstrap';
import Button from '../buttons/button';
import {
  useAddReviewLinkMutation,
  useUpdateReviewLinkMutation,
  useGetReviewByIdQuery,
} from '../../../app/reviewLinksApi/reviewLinksApi';
import CommonModal from '../modalLayout/CommonModal';
import InputWithLabel from '../fields/InputWithLabel';
import { showToast } from '../../../helpers/Utils';
import Loader from '../loader/Loader';

const CreateLinkModal = ({ show, setShow, reviewLinkId }) => {
  const [link, setLink] = useState('');
  const [linktext, setLinkText] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // New state for image preview

  const [createLink, { isLoading: creating }] = useAddReviewLinkMutation();
  const [updateLink, { isLoading: updating }] = useUpdateReviewLinkMutation();
  const { data, isLoading: loadingData, isFetching } = useGetReviewByIdQuery(reviewLinkId, {
    skip: !reviewLinkId,
  });

  // Prefill in edit mode and set image preview
  useEffect(() => {
    if (data?.data && reviewLinkId) {
      setLink(data.data.review_link || '');
      setLinkText(data.data.text || '');
      setImage(data.data.review_image || null);
      if (data.data.review_image) {
        setImagePreview(data.data.review_image);
      }
    } else {
      // Reset fields when creating
      setLink('');
      setImage(null);
      setImagePreview(null);
      setLinkText("")
    }
  }, [data, reviewLinkId]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!link.startsWith('https://') && !link.startsWith('http://')) {
      showToast('Please enter a valid link starting with https:// or http://', 'error');
      return;
    }
    else if  (!linktext || linktext.trim() === "") {
    showToast("Please enter link text", "error");
    return;
  }  

    const formData = new FormData();
    formData.append('review_link', link);
    formData.append('text', linktext);
    if (image) formData.append('review_image', image);

    try {
      if (reviewLinkId) {
        await updateLink({ reviewLinkId, formData }).unwrap();
      } else {
        await createLink(formData).unwrap();
      }
      setShow(false);
      setLink('');
      setImage(null);
      setImagePreview(null);
    } catch (err) {
      showToast('error', err?.data?.message || 'Operation failed');
    }
  };

  return (
    <CommonModal
      show={show}
      setShow={setShow}
      title={reviewLinkId ? 'Edit Review Link' : 'Create Review Link'}
    >
      {(loadingData && reviewLinkId) || isFetching ? (
        <Loader />
      ) : (
        <Form onSubmit={handleSubmit}>
          <InputWithLabel
            label="Review Link"
            type="text"
            placeholder="https://example.com"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            name="review_link"
          />
          <InputWithLabel
            label="Review text"
            type="text"
            placeholder="Goole, Facebook etc."
            value={linktext}
            onChange={(e) => setLinkText(e.target.value)}
            name="text"
          />

          <InputWithLabel
            label="Upload Image"
            type="file"
            onChange={handleImageChange}
            accept="image/*" 
          />

          {/* Show image preview if available */}
          {imagePreview && (
            <div className="my-3">
              <Image src={imagePreview} alt="Preview" fluid width={200} />
            </div>
          )}

          {/* Show existing image if editing and no new image selected */}
          {image && typeof image === 'string' && !imagePreview && (
            <div className="my-3">
              <Image src={image} alt="Existing" fluid width={200} />
            </div>
          )}

          <div className="text-end mt-3">
            <Button
              label="Cancel"
              className="borderedBtn me-3"
              disabled={creating || updating}
              onClick={() => setShow(false)}
            />
            <Button
              label={
                creating || updating
                  ? reviewLinkId
                    ? 'Updating...'
                    : 'Creating...'
                  : reviewLinkId
                  ? 'Update Link'
                  : 'Create Link'
              }
              type="submit"
              variant="primary"
              disabled={creating || updating}
            />
          </div>
        </Form>
      )}
    </CommonModal>
  );
};

export default CreateLinkModal;